/*
 * How a "yes / no, and if yes explain" answer is written down.
 *
 * ⚠️ ONE DEFINITION, for the same reason as pricing.ts next door: the booking page composes
 * this string, the API validates it, and the operator reads it. Three places, one format —
 * and if they drifted, the failure would be a customer's answer rejected as malformed or, far
 * worse, an explanation silently dropped from what the operator sees before she drives out.
 *
 * ⚠️ ONE STRING, NOT TWO FIELDS, and that is deliberate. `bookings.intake_answers` is
 * `Record<string, string>` keyed by question id (0024), and a question owns exactly one id.
 * Splitting the explanation into a second synthetic key would invent ids that no question
 * has — so a later export, or the operator's own inbox, would show an answer belonging to
 * nothing. Storing "Yes — she has mild hip stiffness" keeps the record readable by a human,
 * which is all these answers are for; nothing in the platform branches on them.
 */

/**
 * ⚠️ AN EM DASH WITH SPACES, and it matters that it is not a colon or a hyphen. The
 * explanation is free text a customer typed, and it very often contains both — "Yes: allergies"
 * and "Yes - limps a bit" would make the separator ambiguous the moment somebody wrote one.
 * An em dash surrounded by spaces is not something people type into a form field by accident.
 */
export const YES_EXPLAIN_SEPARATOR = ' — ';

export const YES_EXPLAIN_NO = 'No';

/**
 * Compose the stored value. `explanation` is ignored when the answer is no.
 *
 * ⚠️ A YES WITH NO TEXT IS BARE "Yes", NOT "Yes — ". The first version appended the separator
 * unconditionally, so ticking Yes before typing produced "Yes — " — which `parseYesExplain`
 * trims to "Yes —", matching neither branch and reading back as *nothing answered*. The
 * button therefore appeared not to respond at all and the explanation box never opened.
 * Caught on staging, and it is exactly the round-trip the self-check now asserts.
 *
 * ⚠️⚠️ TRIM DECIDES THE SHAPE, IT DOES NOT TOUCH THE TEXT. This used to store
 * `explanation.trim()`, and because the booking page drives its textarea straight through
 * compose → parse on every keystroke, that made the field REFUSE THE SPACE BAR: type
 * "Hates ", compose trims it back to "Hates", parse returns "Hates", React writes it into the
 * textarea, and the space is gone before the next character arrives. A customer got
 * "Hatesthedyer". Reported from the live page 2026-08-12.
 *
 * So the check for "is there anything here at all" still uses trim — a lone space must not
 * produce "Yes — " and reopen the bug above — but what gets stored is exactly what the
 * customer typed. These answers are read by a human and nothing branches on them; her spacing
 * is hers.
 */
export function composeYesExplain(yes: boolean, explanation: string): string {
  if (!yes) return YES_EXPLAIN_NO;
  return explanation.trim() ? `Yes${YES_EXPLAIN_SEPARATOR}${explanation}` : 'Yes';
}

export interface YesExplain {
  /** null when the value is neither a yes nor a no — i.e. nothing has been answered yet. */
  yes: boolean | null;
  explanation: string;
}

/**
 * Read a stored value back.
 *
 * Tolerant on purpose: a bare "Yes" (from an older client, or a hand-written POST) parses as
 * yes with no explanation, which the validator can then reject on its own terms rather than
 * this function throwing. Parsing and policy are different jobs.
 */
export function parseYesExplain(value: string | undefined | null): YesExplain {
  const raw = value ?? '';
  const v = raw.trim();
  if (!v) return { yes: null, explanation: '' };
  if (v === YES_EXPLAIN_NO) return { yes: false, explanation: '' };
  if (v === 'Yes') return { yes: true, explanation: '' };

  /*
   * ⚠️ THE EXPLANATION COMES BACK VERBATIM — sliced off `raw`, not off the trimmed copy, and
   * not trimmed again afterwards. Both of those would eat a trailing space, and this function
   * is on the keystroke path (see composeYesExplain): whatever it drops, the customer cannot
   * type. The prefix is matched against the trimmed value so a hand-written POST with stray
   * outer whitespace still parses, then the text is taken from the original.
   */
  const prefix = `Yes${YES_EXPLAIN_SEPARATOR}`;
  if (v.startsWith(prefix)) {
    return { yes: true, explanation: raw.slice(raw.indexOf(prefix) + prefix.length) };
  }
  return { yes: null, explanation: '' };
}

/**
 * Is this a complete answer to a "if yes, explain" question?
 *
 * ⚠️ "YES" WITHOUT AN EXPLANATION IS INCOMPLETE, which is the entire point of the type. A
 * customer who ticks yes to "any health concerns?" and writes nothing has told the operator
 * that something is wrong and not what — which is worse than not asking, because it reads as
 * an answered question.
 *
 * "No" alone is always complete. That is what makes this different from a required long-text
 * question, where somebody with a healthy dog has to type "None" to get past.
 */
export function isYesExplainComplete(value: string | undefined | null): boolean {
  const { yes, explanation } = parseYesExplain(value);
  if (yes === null) return false;
  // Trim HERE rather than in the parser: whitespace is not an explanation, but the parser is
  // on the keystroke path and must not edit what is being typed.
  return yes ? explanation.trim().length > 0 : true;
}

/**
 * Whether a "number" answer is acceptable: it has to contain a number, and that is all.
 *
 * ⚠️ UNITS ARE ALLOWED AND KEPT. People write "50 pounds", "about 50", "6 months", "1.5 yrs",
 * and the first version (`Number.isFinite(Number(raw))`) refused every one of them. That
 * cost a live booking on 2026-09-17 (runbook §8.28). The answer is stored as typed, not
 * reduced to its digits, because "6 months" and "6" are different dogs. Nothing in the
 * platform does arithmetic on intake answers; they are there for the operator to read.
 *
 * What is still refused is an answer with no digits at all ("fifty", "not sure"), which is
 * the only case where the question was not really answered.
 */
export function isNumberAnswer(value: string | undefined | null): boolean {
  return /\d/.test(value ?? '');
}
